/* oxlint-disable sorare/enforce-monetary-reward-disclaimer -- Non-reward currency (wallet/market/JONUM limits/etc.) — disclaimer does not apply. */
import type { FormatNumberOptions } from 'react-intl';

import {
  FiatWalletAccountState,
  FiatWalletKycState,
  SCAEnrollmentState,
} from '#src/__generated__/globalTypes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { fiatCurrencySymbols } from '#src/lib/fiat.ts';
import { getMonetaryAmountIndex } from '#src/lib/monetaryAmount.ts';

export const useFiatBalance = (formatOptions?: FormatNumberOptions) => {
  const {
    fiatWalletAccountable: accountable,
    fiatCurrency: fiatCurrencyFromUserSettings,
    currentUser,
  } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const { formatNumber } = useIntlContext();

  const fiatCurrency =
    accountable?.publicInfo.currency || fiatCurrencyFromUserSettings;

  const availableBalanceMonetaryAmountParams = toMonetaryAmount(
    currentUser?.availableBalances?.[getMonetaryAmountIndex(fiatCurrency)]
  );

  const availableBalance =
    availableBalanceMonetaryAmountParams.toMajorUnit(fiatCurrency);
  const availableBalanceInCents =
    availableBalanceMonetaryAmountParams.readAmount(fiatCurrency);

  const availableBalanceWithCurrencySymbol = formatNumber(availableBalance, {
    style: 'currency',
    currency: fiatCurrency,
    ...formatOptions,
  });

  const needScaValidation =
    currentUser?.mangopayUser?.scaEnrollmentState ===
    SCAEnrollmentState.SCA_PENDING_CONFIRMATION;

  const isFiatWalletOwner =
    !!accountable &&
    [
      FiatWalletAccountState.OWNER,
      FiatWalletAccountState.VALIDATED_OWNER,
    ].includesValue(accountable.state);

  const canListAndTrade = isFiatWalletOwner && !needScaValidation;

  const canDepositAndWithdraw =
    !!accountable &&
    accountable.state === FiatWalletAccountState.VALIDATED_OWNER &&
    !needScaValidation;

  const kycUnderReview =
    !!accountable?.kycStatus &&
    [
      FiatWalletKycState.VALIDATION_ASKED,
      FiatWalletKycState.CREATED,
    ].includesValue(accountable.kycStatus);

  const kycCompleted =
    !kycUnderReview &&
    accountable?.state === FiatWalletAccountState.VALIDATED_OWNER;

  return {
    kycStatus: accountable?.kycStatus || undefined,
    kycUnderReview,
    kycCompleted,
    refusedReason: accountable?.kycRefusedReason || undefined,
    fiatWalletState: accountable?.state,
    canPay: !!accountable,
    isFiatWalletOwner,
    canListAndTrade,
    canDepositAndWithdraw,
    fiatBalanceStatus: accountable?.state,
    availableBalance,
    availableBalanceInCents,
    fiatCurrency,
    fiatCurrencySymbol: fiatCurrencySymbols[fiatCurrency],
    availableBalanceWithCurrencySymbol,
    fiatWalletAccountable: accountable,
  };
};
